import type { Edge, Node } from "@xyflow/react";

import type { Deployment } from "../../../types/strategyDeploy";
import type { WorkspaceEdge, WorkspaceNode } from "../../../types/workspace";
import { WORKSPACE_GRAPH_ROW_GAP } from "./workspaceGraphConstants";
import { strategyIdForCombineCandidate } from "./workspaceCombinedPolicyUtils";
import {
  WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT,
  WORKSPACE_EXECUTING_ACCOUNT_NODE_WIDTH,
  computeExecutingAccountNodePosition,
  listPolicyExecutingAccounts,
  resolveExecutingAccountEdgeDetourY,
} from "./workspaceExecutingAccountUtils";

/** Ephemeral React Flow node: a policy version on Launchpad (running, or "Launch" when not). */
export const WORKSPACE_LAUNCHPAD_NODE_TYPE = "workspaceLaunchpad";

export type WorkspaceLaunchpadNodeData = {
  parentNode: WorkspaceNode;
  /** Policy version id (the policy node's entity id). */
  policyVersionId: string;
  /** Strategies-page strategy the version belongs to (null when not resolvable on this canvas). */
  strategyId: string | null;
  /** Deployments running / paused this version (newest first); empty = not launched. */
  deployments: Deployment[];
  isSelected?: boolean;
  isParentSelected?: boolean;
  focusFilterDimmed?: boolean;
};

export function isWorkspaceLaunchpadNodeId(nodeId: string): boolean {
  return nodeId.startsWith("launchpad:");
}

export function launchpadNodeId(policyNodeId: string): string {
  return `launchpad:${encodeURIComponent(policyNodeId)}`;
}

function isPolicyNode(node: WorkspaceNode): boolean {
  return (node.type === "policy" || node.type === "combined_policy") && Boolean(node.entityId) && !node.isDisabled;
}

/** Active (running / paused) deployments by the policy version they run. */
export function deploymentsByPolicyVersion(deployments: Deployment[]): Map<string, Deployment[]> {
  const map = new Map<string, Deployment[]>();
  for (const d of deployments) {
    const vid = d.strategy_snapshot?.policy?.version_id;
    if (!vid || !["running", "paused", "error"].includes(d.status)) continue;
    map.set(vid, [...(map.get(vid) ?? []), d]);
  }
  return map;
}

/** Below the policy's executing-account diamonds (same column), or level with the policy. */
function launchpadPosition(
  parent: WorkspaceNode,
  accountCount: number,
  edges: WorkspaceEdge[],
  nodes: WorkspaceNode[],
  options?: { viewMode?: "account" | "strategy"; maxSimulationsPerReport?: number }
) {
  const opts = { viewMode: options?.viewMode, maxSimulationsPerReport: options?.maxSimulationsPerReport };
  if (accountCount === 0) return computeExecutingAccountNodePosition(parent, 0, 1, edges, nodes, opts);
  const last = computeExecutingAccountNodePosition(parent, accountCount - 1, accountCount, edges, nodes, opts);
  return { x: last.x, y: last.y + WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT + WORKSPACE_GRAPH_ROW_GAP };
}

/** Policies that get a Launchpad node: those already running, and those whose strategy was saved
 *  from the Strategy Lab (only Lab rules can run, so other policies get no Launch suggestion). */
function launchablePolicyNodes(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  byVersion: Map<string, Deployment[]>,
  labStrategyIds: Set<string>
): WorkspaceNode[] {
  return nodes.filter((n) => {
    if (!isPolicyNode(n)) return false;
    if ((byVersion.get(n.entityId) ?? []).length > 0) return true;
    const strategyId = strategyIdForCombineCandidate(n, nodes, edges);
    return Boolean(strategyId && labStrategyIds.has(strategyId));
  });
}

/** One Launchpad node per launched policy / combined policy, in its executing-account column
 *  (below the account diamonds). Only for the current policies of the canvas — archived ones get none. */
export function toLaunchpadFlowNodes(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  byVersion: Map<string, Deployment[]>,
  options?: {
    labStrategyIds?: Set<string>;
    viewMode?: "account" | "strategy";
    selectedNodeId?: string | null;
    focusMatchedNodeIds?: Set<string> | null;
    maxSimulationsPerReport?: number;
  }
): Array<Node<WorkspaceLaunchpadNodeData>> {
  return launchablePolicyNodes(nodes, edges, byVersion, options?.labStrategyIds ?? new Set()).map((parent) => {
    const accounts = listPolicyExecutingAccounts(parent);
    const id = launchpadNodeId(parent.id);
    const isSelected = options?.selectedNodeId === id;
    return {
      id,
      type: WORKSPACE_LAUNCHPAD_NODE_TYPE,
      position: launchpadPosition(parent, accounts.length, edges, nodes, options),
      width: WORKSPACE_EXECUTING_ACCOUNT_NODE_WIDTH,
      height: WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT,
      draggable: false,
      selectable: true,
      connectable: false,
      focusable: false,
      selected: isSelected,
      data: {
        parentNode: parent,
        policyVersionId: parent.entityId,
        strategyId: strategyIdForCombineCandidate(parent, nodes, edges),
        deployments: byVersion.get(parent.entityId) ?? [],
        isSelected,
        isParentSelected: options?.selectedNodeId === parent.id,
        focusFilterDimmed: options?.focusMatchedNodeIds != null && !options.focusMatchedNodeIds.has(parent.id),
      },
    };
  });
}

/** Pipeline edges policy → its Launchpad node (dashed while nothing runs). */
export function toLaunchpadFlowEdges(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  byVersion: Map<string, Deployment[]>,
  labStrategyIds: Set<string> = new Set()
): Edge[] {
  return launchablePolicyNodes(nodes, edges, byVersion, labStrategyIds).map((parent) => {
    const detour = resolveExecutingAccountEdgeDetourY(parent, edges, nodes);
    const running = (byVersion.get(parent.entityId) ?? []).length > 0;
    return {
      id: `launchpad-edge:${encodeURIComponent(parent.id)}`,
      source: parent.id,
      target: launchpadNodeId(parent.id),
      sourceHandle: "source",
      targetHandle: "target",
      type: "workspacePipeline",
      selectable: false,
      focusable: false,
      style: running ? undefined : { strokeDasharray: "4 4", opacity: 0.6 },
      data: detour != null ? { execDetourY: detour } : undefined,
    };
  });
}
